import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { SyncLangueCompte } from "./components/SyncLangueCompte";
import { Toaster } from "./components/ui/sonner";
import VersionChecker from "./components/VersionChecker";
import ConnectionStatusBanner from "./components/ConnectionStatusBanner";
import EnvironmentBanner from "./components/EnvironmentBanner"; // Tâche 1298 — préprod vs prod
import ConnectionRecoveringBanner from "./components/ConnectionRecoveringBanner";
import RateLimitToast from "./components/RateLimitToast";
import LlmBudgetBanner from "./components/LlmBudgetBanner";
import GlobalJobWatcher from "./components/GlobalJobWatcher"; // Vincent B948
import ConnectionDiagnostic from "./components/ConnectionDiagnostic";
import ErrorBoundary from "./components/ErrorBoundary";
import BootHealthCheck from "./components/BootHealthCheck";
import FirstLoginPrompt from "./components/FirstLoginPrompt";
import AIPackageOnboardingWizard from "./components/AIPackageOnboardingWizard";
import { canDesignContent, canEditContent } from "./utils/permissions";

// Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
// Eric (2026-06-16 — iter106) — Page status PUBLIQUE no-auth
import StatusPage from "./pages/StatusPage";
// Eric (2026-06-16 — iter107) — Page diagnostic de visibilité des parcours
import VisibilityDiagnosticPage from "./pages/VisibilityDiagnosticPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import UploadPage from "./pages/UploadPage";
import UploadTextPage from "./pages/UploadTextPage";
import KnowledgePage from "./pages/KnowledgePage";
import KnowledgeTrashPage from "./pages/KnowledgeTrashPage";
import ValidationRapidePage from "./pages/ValidationRapidePage";
import BilanKBPage from "./pages/BilanKBPage";
import ProceduresPage from "./pages/ProceduresPage";
import ChatPage from "./pages/ChatPage";
import ReviewQueuePage from "./pages/ReviewQueuePage";
import AnalyticsPage from "./pages/AnalyticsPage";
import VideoDetailPage from "./pages/VideoDetailPage";
import LearnerVideoPage from "./pages/LearnerVideoPage";
import JargonPage from "./pages/JargonPage";
import JargonTrashPage from "./pages/JargonTrashPage";
import JargonEnrichmentBatchPage from "./pages/JargonEnrichmentBatchPage"; // Tâche 1315
import JargonJournalPage from "./pages/JargonJournalPage"; // Tâche 1316
import OrthographePage from "./pages/OrthographePage"; // Tâche 1443
import QuizPage from "./pages/QuizPage";
import ExternalResourcesPage from "./pages/ExternalResourcesPage";
import MyFavoritesPage from "./pages/MyFavoritesPage"; // Vincent B877 — page favoris globale
// Eric (2026-06-14 LOT J) — Dead imports retirés (AgoraPage, OnboardingPage,
// PathwaysPage) : leurs URLs sont servies par d'autres composants
// (MessagingPage pour /agora, PathwaysRouter pour /onboarding et /learning-path).
import PartnerCodesPage from "./pages/PartnerCodesPage";
import SettingsPage from "./pages/SettingsPage";
import AIPackagePage from "./pages/AIPackagePage";
import EmailSystemStatusPage from "./pages/EmailSystemStatusPage";
// Vincent #163/#164/#165/#168 (2026-05-24)
// (PathwaysPage import removed — served by PathwaysRouter)
import LearnerPathwaysView from "./pages/LearnerPathwaysView";
import PathwaysRouter from "./pages/PathwaysRouter";
// Vincent XLSX 2026-07-13 21h Row 472 — Matrice d'accès aux parcours
import PathwayAccessMatrixPage from "./pages/PathwayAccessMatrixPage";
import CouponsPage from "./pages/CouponsPage";
import AIPackageConsumptionPage from "./pages/AIPackageConsumptionPage";
import AIPackageAuditPage from "./pages/AIPackageAuditPage";
import AdminMigrationsPage from "./pages/AdminMigrationsPage";
import StopwordsAdminPage from "./pages/admin/StopwordsAdminPage";
// Vincent B784 (Eric 2026-02-18 SPEC STRICK PROD) — Batch detect legacy aggressive_dedup.
import AggressiveDedupAdminPage from "./pages/admin/AggressiveDedupAdminPage";
import AggressiveWhisperFilterAdminPage from "./pages/admin/AggressiveWhisperFilterAdminPage";
// Vincent B823 (Eric 2026-02-13) — Page admin d'inspection/backfill des embeddings RAG.
import EmbeddingsAdminPage from "./pages/admin/EmbeddingsAdminPage";
import CacheInspectorPage from "./pages/admin/CacheInspectorPage";
import TagMergesAdminPage from "./pages/admin/TagMergesAdminPage"; // Vincent B905
import TagAnalyticsAdminPage from "./pages/admin/TagAnalyticsAdminPage"; // Vincent B907
import AdminHealthDashboardPage from "./pages/admin/AdminHealthDashboardPage"; // Vincent B1020
import LoginSentinelPage from "./pages/admin/LoginSentinelPage"; // B1053 — Sentinelle Connexion
import EmailSendLogPage from "./pages/admin/EmailSendLogPage"; // Tâche 1143 — Journal des envois d'alertes
import UnsubscribePage from "./pages/UnsubscribePage"; // Tâche 1147 — Désabonnement public
import IncidentJournalPage from "./pages/admin/IncidentJournalPage"; // B1055 — Journal d'incidents
import SynthesisVerificationsPage from "./pages/admin/SynthesisVerificationsPage"; // B1067 — Historique des vérifications de synthèses
import AxiosTransportCanary from "./components/AxiosTransportCanary"; // B1053 — canari de transport (aurait attrapé B1052)
import TagsPage from "./pages/TagsPage"; // Vincent B963 (Eric 2026-02-14)
import DictRejectedTermsPage from "./pages/admin/DictRejectedTermsPage"; // Vincent B967 (Eric 2026-02-14)
import FirefliesMaintenancePage from "./pages/admin/FirefliesMaintenancePage"; // Vincent B912
import SpeakerVariantsAdminPage from "./pages/admin/SpeakerVariantsAdminPage"; // Vincent B940
import FactualCorrectionsAdminPage from "./pages/admin/FactualCorrectionsAdminPage"; // Vincent B946
import SearchResultsPage from "./pages/SearchResultsPage"; // Vincent B906
import UploadMaintenancePage from "./pages/UploadMaintenancePage";
import AIPackageGuidePage from "./pages/AIPackageGuidePage";
import RoleLabelsPage from "./pages/RoleLabelsPage";
import AdminDiagnosticsPage from "./pages/AdminDiagnosticsPage";
// Vincent B982 (Eric 2026-02-26 SPEC STRICK PROD) — Page dédiée
// « Procédures : regénération massive » extraite de Diagnostics.
import ProceduresRegenerationPage from "./pages/admin/ProceduresRegenerationPage";
import AdminAlertsPage from "./pages/AdminAlertsPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import LandingPage from "./pages/LandingPage";
import AIVideoPage from "./pages/AIVideoPage";
import SharedVideoPage from "./pages/SharedVideoPage";
import PricingPage from "./pages/PricingPage";
import PublicDemoPage from "./pages/PublicDemoPage";
import BillingPage from "./pages/BillingPage";
import TeamAnalyticsPage from "./pages/TeamAnalyticsPage";
import InvitePage from "./pages/InvitePage";
import CertificationsPage from "./pages/CertificationsPage";
import VideoQueuePage from "./pages/VideoQueuePage";
import ContentCategoriesPage from "./pages/ContentCategoriesPage";
import VincentBatchesPage from "./pages/VincentBatchesPage";
import QuestionnairePage from "./pages/QuestionnairePage";
import FirewallPage from "./pages/FirewallPage";
import TechStackPage from "./pages/TechStackPage";
import AuditLogPage from "./pages/AuditLogPage";
import FeatureFlagsPage from "./pages/FeatureFlagsPage";
import PlatformDashboardPage from "./pages/PlatformDashboardPage";
import PlatformBYOKOverviewPage from "./pages/PlatformBYOKOverviewPage";
import PlatformOrgsPage from "./pages/PlatformOrgsPage";
import PlatformOrgMembersPage from "./pages/PlatformOrgMembersPage";
import AccessMatrixPage from "./pages/AccessMatrixPage";
import BackupsPage from "./pages/BackupsPage";
import StrategicReportPage from "./pages/StrategicReportPage";
import AMFCompliancePage from "./pages/AMFCompliancePage";
import ComplianceScoringPage from "./pages/ComplianceScoringPage";
import UserApprovalPage from "./pages/UserApprovalPage";
import SpeakersPage from "./pages/SpeakersPage";
// (AgoraPage import removed — served by MessagingPage)
import MessagingPage from "./pages/MessagingPage";
import ChatbotSourcesPage from "./pages/ChatbotSourcesPage";
import ChatbotGapsPage from "./pages/ChatbotGapsPage";
import { NavHistoryTracker } from "./hooks/useNavBack";
import PrivacyPage from "./pages/PrivacyPage";
import DataValidationPage from "./pages/DataValidationPage";
import TranscriptionsListPage from "./pages/TranscriptionsListPage";
import CoverageGapsPage from "./pages/CoverageGapsPage";
import TermsPage from "./pages/TermsPage";
import KnowledgeImportPage from "./pages/KnowledgeImportPage";
import ConfidentialiteIAPage from "./pages/ConfidentialiteIAPage";
import NotFoundPage from "./pages/NotFoundPage";
import SecurityCompliancePage from "./pages/SecurityCompliancePage";
import AgentPage from "./pages/AgentPage";
import AgentDraftReviewPage from "./pages/AgentDraftReviewPage";
import FileExplorerPage from "./pages/FileExplorerPage";
import ScriptsPage from "./pages/ScriptsPage";
import ConcepteurOnboardingPage from "./pages/ConcepteurOnboardingPage";
import DiscoveryPage from "./pages/DiscoveryPage";
import TierManagementPage from "./pages/TierManagementPage";
import DemoTourPage from "./pages/DemoTourPage";
import PreferencesPage from "./pages/PreferencesPage";
// Vincent #328 (Eric 2026-06-17) — BugTrackerPage retirée du menu et des routes.

// Layouts
import DashboardLayout from "./layouts/DashboardLayout";

import "./App.css";

function ProtectedRoute({ children, adminOnly = false, editorOnly = false, designerOnly = false }) {
  const { user, loading, authReachable, retryAuth } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="auth-loading">
        <div className="animate-spin w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full" />
      </div>
    );
  }

  // Vincent bug #38 (2026-05-07) — never redirect to /login on a transient backend
  // hiccup. `user === null` means "we couldn't reach /api/auth/me but we don't know
  // for sure the session is gone". Show a recoverable banner instead of ejecting.
  if (user === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-4 py-8"
        data-testid="auth-unreachable"
      >
        <div className="max-w-2xl w-full bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="text-center mb-5">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 text-2xl">⚠</div>
            <h2 className="text-lg font-semibold text-zinc-900 mb-1">Connexion temporairement indisponible</h2>
            <p className="text-sm text-zinc-600">
              Knoria n'arrive pas à joindre le serveur. <strong>Votre session reste intacte.</strong>
              Le diagnostic ci-dessous identifie automatiquement la cause.
            </p>
          </div>

          <ConnectionDiagnostic userEmail="" />

          <div className="flex gap-2 justify-center mt-5 pt-5 border-t border-zinc-100">
            <button
              onClick={() => retryAuth()}
              className="px-4 py-2 bg-[#002FA7] text-white rounded-md text-sm font-medium hover:bg-[#001f7a] transition-colors"
              data-testid="auth-retry-btn"
            >
              Réessayer la connexion
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-zinc-100 text-zinc-700 rounded-md text-sm font-medium hover:bg-zinc-200 transition-colors"
              data-testid="auth-reload-btn"
            >
              Recharger la page
            </button>
          </div>
          {authReachable === false && (
            <p className="text-[11px] text-zinc-400 mt-3 text-center">Backend injoignable — voir le diagnostic ci-dessus.</p>
          )}
        </div>
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  // Vincent #370 (Eric 2026-06-22 SPEC STRICK) — Le concepteur doit accéder
  // aux pages "Concepteurs" (Validation rapide, Validation des données,
  // Transcription, Locuteurs, File de révision, Scoring AMF) au même titre
  // qu'un admin/fondateur.
  // iter114 (Eric 2026-06-24) — Migration vers helper canonique canEditContent
  // (single source of truth aligné avec backend require_editor). Évite tout
  // futur drift de whitelist comme celui qui a causé le bug Etienne iter112.
  if (editorOnly && !canEditContent(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Tâche 1443 (Eric 2026-10-02) — Page Orthographe : concepteur, admin et
  // fondateur UNIQUEMENT, les rôles avancé / intermédiaire / débutant exclus
  // même en lecture. La vraie barrière est `require_designer` au serveur ;
  // cette redirection évite seulement d'afficher un écran d'erreur.
  if (designerOnly && !canDesignContent(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full" />
      </div>
    );
  }

  // Eric iter83 — SECURITY: never bypass /register when an invite token is present.
  // Un founder déjà connecté qui clique sur le lien d'invitation d'un autre user
  // doit voir le formulaire d'inscription (et non être redirigé vers son propre
  // dashboard), sinon le compte invité n'est jamais créé.
  const hasInviteToken =
    location.pathname.startsWith("/register") &&
    (location.search.includes("invite=") || location.search.includes("token="));

  if (user && !hasInviteToken) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Landing page - public */}
      <Route path="/" element={<LandingPage />} />
      {/* Eric (2026-02-06) — Page publique de capture de leads. Lien partageable
          knoria.com/demo. Pas d'auth. Cible : secteurs régulés (assurance,
          comptables, planificateurs, courtiers immobiliers, juridique, santé). */}
      <Route path="/demo" element={<PublicDemoPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />

      {/* Tâche 1147 — Désabonnement depuis un courriel : aucune session
          requise (jeton signé dans l'URL), donc hors <PublicRoute> qui
          redirigerait un utilisateur déjà connecté. */}
      <Route path="/desabonnement/:token" element={<UnsubscribePage />} />

      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      {/* Vincent #288 (2026-06-11) — Réinitialisation de mot de passe via lien
          courriel envoyé par un gestionnaire (token 1h à usage unique). */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Eric (2026-06-16 — iter106) — Page de status publique, no-auth */}
      <Route path="/status" element={<StatusPage />} />

      {/* Protected routes with dashboard layout */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="upload" element={<UploadPage />} />
        {/* Vincent #302 (2026-06-12) — Importer un fichier textuel (.txt/.md/.docx/.pdf). */}
        <Route path="upload-text" element={<UploadTextPage />} />
        <Route path="knowledge" element={<KnowledgePage />} />
        {/* Vincent #17 batch FLHQ (Eric 2026-02-06) — Corbeille BC */}
        <Route path="knowledge/trash" element={<KnowledgeTrashPage />} />
        <Route path="validation-rapide" element={<ValidationRapidePage />} />
        <Route path="bilan-kb" element={<BilanKBPage />} />
        {/* Vincent #250 (2026-02-06) — La page /dashboard/procedures redirige
            maintenant vers l'onglet "Procédures" de la base de connaissances,
            pour avoir une source unique de vérité (au lieu de 2 listes
            divergentes). L'ancien ProceduresPage reste accessible via
            /dashboard/procedures-legacy pour rollback rapide si besoin. */}
        <Route path="procedures" element={<Navigate to="/dashboard/knowledge?tab=procedure" replace />} />
        <Route path="procedures-legacy" element={<ProceduresPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="quiz" element={<QuizPage />} />
        <Route path="external-resources" element={<ExternalResourcesPage />} />
        {/* Vincent B877 (Eric 2026-02-14 SPEC STRICK PROD) — Page globale
            « Mes favoris » (toutes vidéos, tous types) dans le menu Apprentissage. */}
        <Route path="favorites" element={<MyFavoritesPage />} />
        <Route path="onboarding" element={<PathwaysRouter pathwayType="onboarding" />} />
        <Route path="learning-path" element={<PathwaysRouter pathwayType="learning" />} />
        {/* Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) — 3e type de parcours */}
        <Route path="weekly-trainings" element={<PathwaysRouter pathwayType="weekly" />} />
        {/* Vincent XLSX 2026-07-13 21h Row 472 — Matrice d'accès (founder/admin/concepteur/employee_advanced) */}
        <Route path="pathway-access" element={<ProtectedRoute editorOnly><PathwayAccessMatrixPage /></ProtectedRoute>} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        <Route path="ai-video" element={<AIVideoPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="billing/coupons" element={<CouponsPage />} />
        <Route path="team-analytics" element={<TeamAnalyticsPage />} />
        <Route path="certifications" element={<CertificationsPage />} />
        <Route path="video-queue" element={<VideoQueuePage />} />
        <Route path="data-validation" element={<ProtectedRoute editorOnly><DataValidationPage /></ProtectedRoute>} />
        <Route path="transcriptions" element={<ProtectedRoute editorOnly><TranscriptionsListPage /></ProtectedRoute>} />
        {/* Tâche 1278 — Le tableau de bord des écarts a son propre écran. */}
        <Route path="gaps" element={<ProtectedRoute editorOnly><CoverageGapsPage /></ProtectedRoute>} />
        <Route path="knowledge/import" element={<ProtectedRoute editorOnly><KnowledgeImportPage /></ProtectedRoute>} />
        <Route path="agent" element={<AgentPage />} />
        <Route path="agent/drafts" element={<ProtectedRoute editorOnly><AgentDraftReviewPage /></ProtectedRoute>} />
        <Route path="discovery" element={<DiscoveryPage />} />
        <Route path="admin/tiers" element={<ProtectedRoute adminOnly><TierManagementPage /></ProtectedRoute>} />
        <Route path="explorer" element={<FileExplorerPage />} />
        {/* Vincent XLSX 2026-07-20 Row 505 (Eric SPEC STRICK) — Le menu
            « Scripts de vente » à gauche pointait vers ScriptsPage (une 2e
            page dupliquée avec des menus manquants). On redirige vers la
            page Base de connaissances filtrée sur type=script — même
            comportement que la ligne « procedures » juste au-dessus. */}
        <Route path="scripts" element={<Navigate to="/dashboard/knowledge?tab=script" replace />} />
        <Route path="video/:videoId/scripts" element={<ScriptsPage />} />
        <Route path="concepteur/onboarding" element={<ConcepteurOnboardingPage />} />
        <Route path="videos/:videoId" element={<VideoDetailPage />} />
        {/* Vincent #292 / #297 (2026-06-12) — Vue minimale apprenant pour les
            vidéos consultées depuis un parcours (utilisée aussi par "Visualiser"). */}
        <Route path="learner/videos/:videoId" element={<LearnerVideoPage />} />
        <Route path="categories" element={<ContentCategoriesPage />} />
        <Route path="admin/vincent-batches" element={<VincentBatchesPage />} />
        {/* Vincent B905 (Eric 2026-02-14) — Fusion de tags quasi-doublons */}
        <Route path="admin/tag-merges" element={<ProtectedRoute adminOnly><TagMergesAdminPage /></ProtectedRoute>} />
        {/* Vincent B907 (Eric 2026-02-14) — Analytics des tags recherchés */}
        <Route path="admin/tag-analytics" element={<ProtectedRoute adminOnly><TagAnalyticsAdminPage /></ProtectedRoute>} />
        {/* Vincent B1020 (Eric 2026-02-26 SPEC STRICK PROD) — Dashboard santé app admin */}
        <Route path="admin/health" element={<ProtectedRoute adminOnly><AdminHealthDashboardPage /></ProtectedRoute>} />
        {/* B1053 (Eric 2026-09-04) — Sentinelle Connexion : vrai parcours de
            connexion toutes les 5 min + canari de transport navigateur. */}
        <Route path="admin/login-sentinel" element={<ProtectedRoute adminOnly><LoginSentinelPage /></ProtectedRoute>} />
        <Route path="admin/email-send-log" element={<ProtectedRoute adminOnly><EmailSendLogPage /></ProtectedRoute>} />
        {/* B1055 (Eric 2026-09-04) — Journal d'incidents : chaque panne avec
            sa cause réelle et son correctif, éditables. */}
        <Route path="admin/incidents" element={<ProtectedRoute adminOnly><IncidentJournalPage /></ProtectedRoute>} />
        <Route path="admin/synthesis-verifications" element={<ProtectedRoute adminOnly><SynthesisVerificationsPage /></ProtectedRoute>} />
        <Route path="tags" element={<ProtectedRoute><TagsPage /></ProtectedRoute>} />
        <Route path="admin/dict-rejected" element={<ProtectedRoute><DictRejectedTermsPage /></ProtectedRoute>} />
        {/* Vincent B912 (Eric 2026-02-14) — Maintenance Fireflies (purge rétroactive) */}
        <Route path="admin/fireflies-maintenance" element={<ProtectedRoute adminOnly><FirefliesMaintenancePage /></ProtectedRoute>} />
        {/* Vincent B940 (Eric 2026-02-14) — Fusion de locuteurs cross-vidéos */}
        <Route path="admin/speaker-variants" element={<ProtectedRoute adminOnly><SpeakerVariantsAdminPage /></ProtectedRoute>} />
        {/* Vincent B946 (Eric 2026-02-14) — Historique des corrections factuelles (audit Loi 25) */}
        <Route path="admin/factual-corrections" element={<ProtectedRoute adminOnly><FactualCorrectionsAdminPage /></ProtectedRoute>} />
        {/* Vincent B906 (Eric 2026-02-14) — Page complète des résultats de recherche par tags */}
        <Route path="search" element={<SearchResultsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/ai-package" element={<ProtectedRoute adminOnly><AIPackagePage /></ProtectedRoute>} />
        <Route path="admin/email-system-status" element={<ProtectedRoute adminOnly><EmailSystemStatusPage /></ProtectedRoute>} />
        {/* Eric (2026-06-16 — iter107) — Diagnostic admin pour visibilité des parcours utilisateurs */}
        <Route path="admin/visibility-diagnostic" element={<ProtectedRoute adminOnly><VisibilityDiagnosticPage /></ProtectedRoute>} />
        <Route path="settings/ai-package/consumption" element={<ProtectedRoute adminOnly><AIPackageConsumptionPage /></ProtectedRoute>} />
        <Route path="settings/ai-package/audit" element={<ProtectedRoute adminOnly><AIPackageAuditPage /></ProtectedRoute>} />
        <Route path="admin/migrations" element={<ProtectedRoute adminOnly><AdminMigrationsPage /></ProtectedRoute>} />
        <Route path="admin/upload-maintenance" element={<ProtectedRoute adminOnly><UploadMaintenancePage /></ProtectedRoute>} />
        <Route path="settings/ai-package/guide" element={<ProtectedRoute adminOnly><AIPackageGuidePage /></ProtectedRoute>} />
        <Route path="ai-package" element={<ProtectedRoute adminOnly><AIPackagePage /></ProtectedRoute>} />
        <Route path="ai-package/consumption" element={<ProtectedRoute adminOnly><AIPackageConsumptionPage /></ProtectedRoute>} />
        <Route path="ai-package/guide" element={<ProtectedRoute adminOnly><AIPackageGuidePage /></ProtectedRoute>} />
        <Route path="admin/role-labels" element={<ProtectedRoute adminOnly><RoleLabelsPage /></ProtectedRoute>} />
        <Route path="admin/diagnostics" element={<ProtectedRoute adminOnly><AdminDiagnosticsPage /></ProtectedRoute>} />
        <Route path="admin/procedures-regeneration" element={<ProtectedRoute><ProceduresRegenerationPage /></ProtectedRoute>} />
        {/* BOGUE 553 (Eric 2026-07-25) — Rejeu diarisation vidéos historiques */}
        <Route path="admin/alerts" element={<ProtectedRoute adminOnly><AdminAlertsPage /></ProtectedRoute>} />
        <Route path="video/:videoId" element={<VideoDetailPage />} />
        <Route path="amf-compliance" element={<AMFCompliancePage />} />
        
        {/* Admin routes — Vincent #370 (Eric 2026-06-22) : "admin/review" et
            "admin/compliance-scoring" sont accessibles au concepteur (editorOnly).
            Les autres routes admin/* restent restreintes au vrai admin/founder. */}
        <Route path="admin/review" element={
          <ProtectedRoute editorOnly>
            <ReviewQueuePage />
          </ProtectedRoute>
        } />
        <Route path="admin/compliance-scoring" element={
          <ProtectedRoute editorOnly>
            <ComplianceScoringPage />
          </ProtectedRoute>
        } />
        <Route path="admin/analytics" element={
          <ProtectedRoute adminOnly>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="admin/jargon" element={
          <ProtectedRoute adminOnly>
            <JargonPage />
          </ProtectedRoute>
        } />
        {/* Tâche 1139 (Eric 2026-06-09) — Corbeille du dictionnaire. Le bouton
            existait déjà sur la page Dictionnaire mais la route était absente :
            il menait à un écran vide. */}
        {/* Tâches 1315 · 1316 (Eric 2026-06-30) — Enrichissement en lot et
            journal du dictionnaire. Le journal est réservé aux
            administrateurs côté serveur ; la route l'est aussi. */}
        <Route path="admin/jargon/enrichment" element={
          <ProtectedRoute>
            <JargonEnrichmentBatchPage />
          </ProtectedRoute>
        } />
        <Route path="admin/jargon/journal" element={
          <ProtectedRoute adminOnly>
            <JargonJournalPage />
          </ProtectedRoute>
        } />
        {/* Tâche 1443 (Eric 2026-10-02) — Page Orthographe : fautes courantes,
            consigne pour l'IA, et détection dans toute la base en un bouton.
            Concepteur / admin / fondateur uniquement. */}
        <Route path="orthographe" element={
          <ProtectedRoute designerOnly>
            <OrthographePage />
          </ProtectedRoute>
        } />
        <Route path="admin/jargon/trash" element={
          <ProtectedRoute adminOnly>
            <JargonTrashPage />
          </ProtectedRoute>
        } />
        {/* Vincent B686 (Eric 2026-02-XX) — Édition admin de la stop-liste jargon. */}
        <Route path="admin/stopwords" element={
          <ProtectedRoute adminOnly>
            <StopwordsAdminPage />
          </ProtectedRoute>
        } />
        {/* Vincent B784 (Eric 2026-02-18 SPEC STRICK PROD) — Batch detect
            legacy `aggressive_dedup` + rerun batch en 1 clic. Editor-only
            (les lecteurs simples n'ont pas accès aux relance Whisper). */}
        <Route path="admin/aggressive-dedup" element={
          <ProtectedRoute editorOnly>
            <AggressiveDedupAdminPage />
          </ProtectedRoute>
        } />
        {/* Vincent B847 (Eric 2026-02-14 SPEC STRICK PROD) — Filtre Whisper
            B789 agressif : batch rerun avec le nouveau filtre combiné B846. */}
        <Route path="admin/aggressive-whisper-filter" element={
          <ProtectedRoute editorOnly>
            <AggressiveWhisperFilterAdminPage />
          </ProtectedRoute>
        } />
        {/* Vincent B823 (Eric 2026-02-13) — Embeddings & RAG (founder-only). */}
        <Route path="admin/embeddings" element={
          <ProtectedRoute adminOnly>
            <EmbeddingsAdminPage />
          </ProtectedRoute>
        } />
        {/* Vincent B687 (Eric 2026-02-08) — Inspecteur du cache TanStack Query. */}
        <Route path="admin/cache-inspector" element={
          <ProtectedRoute adminOnly>
            <CacheInspectorPage />
          </ProtectedRoute>
        } />
        {/* Vincent batch v8 (2026-05-08) — alias consultation Dictionnaire (Général > Connaissances).
            Mêmes données, JargonPage cache l'édition pour les non-admins. */}
        <Route path="dictionary" element={<JargonPage />} />
        {/* Vincent #31/#32 (2026-02) — Alias français pour le dictionnaire,
            cohérent avec le reste de l'UI francophone (KNORIA est québécois). */}
        <Route path="dictionnaire" element={<JargonPage />} />
        <Route path="preferences" element={<PreferencesPage />} />
        <Route path="admin/partners" element={
          <ProtectedRoute adminOnly>
            <PartnerCodesPage />
          </ProtectedRoute>
        } />
        <Route path="admin/firewall" element={
          <ProtectedRoute adminOnly>
            <FirewallPage />
          </ProtectedRoute>
        } />
        <Route path="admin/tech-stack" element={
          <ProtectedRoute adminOnly>
            <TechStackPage />
          </ProtectedRoute>
        } />
        <Route path="platform/audit-logs" element={
          <ProtectedRoute adminOnly>
            <AuditLogPage />
          </ProtectedRoute>
        } />
        <Route path="platform/feature-flags" element={
          <ProtectedRoute adminOnly>
            <FeatureFlagsPage />
          </ProtectedRoute>
        } />
        <Route path="platform/overview" element={
          <ProtectedRoute adminOnly>
            <PlatformDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="platform/byok-overview" element={
          <ProtectedRoute adminOnly>
            <PlatformBYOKOverviewPage />
          </ProtectedRoute>
        } />
        <Route path="platform/organizations" element={
          <ProtectedRoute adminOnly>
            <PlatformOrgsPage />
          </ProtectedRoute>
        } />
        <Route path="platform/organizations/:orgId/members" element={
          <ProtectedRoute adminOnly>
            <PlatformOrgMembersPage />
          </ProtectedRoute>
        } />
        <Route path="admin/access-matrix" element={
          <ProtectedRoute adminOnly>
            <AccessMatrixPage />
          </ProtectedRoute>
        } />
        <Route path="admin/backups" element={
          <ProtectedRoute adminOnly>
            <BackupsPage />
          </ProtectedRoute>
        } />
        <Route path="platform/approvals" element={
          <ProtectedRoute adminOnly>
            <UserApprovalPage />
          </ProtectedRoute>
        } />
        <Route path="speakers" element={
          <ProtectedRoute editorOnly>
            <SpeakersPage />
          </ProtectedRoute>
        } />
        <Route path="agora" element={
          <ProtectedRoute>
            <MessagingPage />
          </ProtectedRoute>
        } />
        <Route path="admin/chatbot-sources" element={
          <ProtectedRoute>
            <ChatbotSourcesPage />
          </ProtectedRoute>
        } />
        <Route path="admin/chatbot-gaps" element={
          <ProtectedRoute>
            <ChatbotGapsPage />
          </ProtectedRoute>
        } />
        <Route path="privacy" element={
          <ProtectedRoute>
            <PrivacyPage />
          </ProtectedRoute>
        } />
      </Route>

      {/* Questionnaire - accessible after login */}
      <Route path="/questionnaire" element={
        <ProtectedRoute>
          <QuestionnairePage />
        </ProtectedRoute>
      } />

      {/* Public shared video */}
      <Route path="/share/:token" element={<SharedVideoPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/confidentialite-ia" element={<ConfidentialiteIAPage />} />
      <Route path="/securite" element={<SecurityCompliancePage />} />
      <Route path="/security" element={<SecurityCompliancePage />} />

      {/* Strategic Report - founders only */}
      <Route path="/rapport-strategique" element={<StrategicReportPage />} />

      {/* Tâche 1396 — La visite guidée était INATTEIGNABLE : elle était
          déclarée sur `/demo`, déjà pris par la page publique de démo plus
          haut dans ce fichier. React Router retient la PREMIÈRE route qui
          correspond, donc `DemoTourPage` n'a jamais été rendue. Découvert en
          voulant vérifier sa traduction. Elle a désormais son propre chemin ;
          `/demo` reste la page publique, qui n'a pas changé. */}
      <Route path="/visite-guidee" element={<ProtectedRoute><DemoTourPage /></ProtectedRoute>} />

      {/* Catch all - friendly 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

// Vincent B685 (Eric 2026-02-XX) — TanStack Query v5 global client.
// staleTime 30s : les données restent "fresh" 30 s avant un refetch automatique
// (limite les requêtes redondantes en navigation onglet ↔ onglet).
// retry 1 : un seul retry sur échec réseau (axios a déjà son propre retry).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Vincent B686 (Eric 2026-02-XX) — DevTools TanStack Query :
  // Le module `-devtools` tente d'instancier `Intl.Locale(navigator.language)`
  // à l'import. Certains navigateurs (préview headless, Playwright...) exposent
  // une chaîne non-canonique et provoquent un RangeError qui écrase la page.
  // → Opt-in via `?devtools=1` uniquement, pour éviter les impacts en prod ET
  // en preview. Vincent (ou Eric) peut activer à la demande.
  const devtoolsEnabled = typeof window !== "undefined"
    && window.location.search.includes("devtools=1");
  return (
    <QueryClientProvider client={queryClient}>
      {devtoolsEnabled && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
      <LanguageProvider>
        <ThemeProvider>
          <BootHealthCheck>
            <AuthProvider>
              {/* Tâche 1329 — La langue du compte s'applique ici, à
                  l'intérieur d'AuthProvider (qui connaît le compte) et de
                  LanguageProvider (qui détient la langue). */}
              <SyncLangueCompte />
              <BrowserRouter>
                <ErrorBoundary>
                  <VersionChecker />
                  {/* B1053 (Eric 2026-09-04) — Canari de transport axios. Monté
                      AVANT toute route (donc actif sur /login) : il compare un
                      GET en axios au même GET en fetch() natif et alerte si
                      axios est mort alors que le navigateur va bien. C'est la
                      seule couche capable de détecter une panne de type B1052,
                      invisible depuis le serveur. */}
                  <AxiosTransportCanary />
                  {/* Tâche 1298 — Bandeau d'environnement, monté avant les
                      routes pour être présent jusque sur /login. */}
                  <EnvironmentBanner />
                  <ConnectionStatusBanner />
                  {/* Vincent 2026-07-17 (Eric SPEC STRICK — stabilité perm. 520) —
                      Banner discret qui informe l'utilisateur pendant les retries
                      automatiques axios (Cloudflare 520, pod K8s redémarre, etc.). */}
                  <ConnectionRecoveringBanner />
                  {/* B1109 (Eric 2026-09-08) — Bannière budget LLM UNIQUE :
                      la barre collée en haut (ex-B800) fusionnée avec les
                      informations de coût et les boutons de l'encadré
                      (ex-B881). Une seule sonde, plus de contradiction. */}
                  <LlmBudgetBanner />
                  <RateLimitToast />
                  <FirstLoginPrompt />
                  <AIPackageOnboardingWizard />
                  <NavHistoryTracker />
                  {/* Vincent B948 (Eric 2026-02-14) — Watcher global des jobs
                      background (bulk-verify-factual, extensible). Émet une
                      notification toast persistante quand un job passe done. */}
                  <GlobalJobWatcher />
                  <AppRoutes />
                  {/* Vincent bug #109 (2026-05-08) — Toaster en haut-droite pour ne pas être masqué
                      par le logo "Made with Emergent" en bas de page. z-[9999] passe au-dessus de tout. */}
                  <Toaster position="top-right" toastOptions={{ className: "z-[9999]" }} />
                </ErrorBoundary>
              </BrowserRouter>
            </AuthProvider>
          </BootHealthCheck>
        </ThemeProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
